// utils/fetchData.js

const API_URL = "https://json-backend.vercel.app/json/6891f64b729c0018e3febefd";

const PUBLIC_URL = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");
const FALLBACK_API_URL = `${PUBLIC_URL}/api.json`;

const REMOTE_TIMEOUT_MS = 1_500;
const FALLBACK_TIMEOUT_MS = 5_000;

const STORAGE_KEY = "apiData";
const STORAGE_META_KEY = "apiDataMeta";
const LOADER_ID = "api-configuration-loader";

let activeRequest = null;

const isBrowser = () => typeof window !== "undefined";

const canUseDOM = () => isBrowser() && typeof document !== "undefined";

/* ---------------- Loader ---------------- */

const showLoader = (message = "Loading application data…") => {
  if (!canUseDOM()) return;

  let loader = document.getElementById(LOADER_ID);

  if (!loader) {
    loader = document.createElement("div");
    loader.id = LOADER_ID;
    loader.setAttribute("role", "status");
    loader.setAttribute("aria-live", "polite");
    loader.setAttribute("aria-atomic", "true");

    const style = document.createElement("style");

    style.textContent = `
      #${LOADER_ID} {
        position: fixed;
        inset: 0;
        z-index: 2147483647;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 18px;
        padding: 24px;
        background: #f8fafc;
        color: #475569;
        font-family: system-ui, sans-serif;
        text-align: center;
        cursor: progress;
      }

      #${LOADER_ID} .api-config-spinner {
        width: 44px;
        height: 44px;
        box-sizing: border-box;
        border: 4px solid #e2e8f0;
        border-top-color: #5046e5;
        border-radius: 50%;
        animation: api-config-spin 0.8s linear infinite;
      }

      #${LOADER_ID} p {
        margin: 0;
        font-size: 15px;
        line-height: 1.5;
      }

      @keyframes api-config-spin {
        to { transform: rotate(360deg); }
      }

      @media (prefers-reduced-motion: reduce) {
        #${LOADER_ID} .api-config-spinner {
          animation: none;
        }
      }
    `;

    const spinner = document.createElement("div");
    spinner.className = "api-config-spinner";
    spinner.setAttribute("aria-hidden", "true");

    const text = document.createElement("p");
    text.className = "api-config-message";

    loader.append(style, spinner, text);
    (document.body || document.documentElement).appendChild(loader);
  }

  loader.querySelector(".api-config-message").textContent = message;
};

const hideLoader = () => {
  if (!canUseDOM()) return;
  document.getElementById(LOADER_ID)?.remove();
};

/* ---------------- Data validation ---------------- */

const extractData = (result) => {
  if (result === null || typeof result !== "object") {
    throw new Error("The response is not a JSON object or array.");
  }

  const data = Object.prototype.hasOwnProperty.call(result, "data")
    ? result.data
    : result;

  if (data === null || typeof data !== "object") {
    throw new Error("The response does not contain a JSON object or array.");
  }

  return data;
};

/* ---------------- Cache ---------------- */

const removeCacheKeys = (...keys) => {
  if (!isBrowser()) return;

  try {
    keys.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Browser storage may be disabled.
  }
};

const storeData = (data, source, isFallback) => {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

    window.localStorage.setItem(
      STORAGE_META_KEY,
      JSON.stringify({
        fetchedAt: new Date().toISOString(),
        source,
        isFallback,
      }),
    );
  } catch {
    // Return fetched data even when storage is unavailable.
  }
};

export const getStoredData = () => {
  if (!isBrowser()) return null;

  try {
    const value = window.localStorage.getItem(STORAGE_KEY);

    if (value === null) return null;

    const data = JSON.parse(value);

    if (data === null || typeof data !== "object") {
      throw new Error("Invalid cached configuration.");
    }

    return data;
  } catch {
    removeCacheKeys(STORAGE_KEY, STORAGE_META_KEY);
    return null;
  }
};

export const getStoredDataMeta = () => {
  if (!isBrowser()) return null;

  try {
    const value = window.localStorage.getItem(STORAGE_META_KEY);

    if (value === null) return null;

    const meta = JSON.parse(value);

    if (
      !meta ||
      typeof meta.fetchedAt !== "string" ||
      typeof meta.source !== "string" ||
      typeof meta.isFallback !== "boolean"
    ) {
      throw new Error("Invalid cache metadata.");
    }

    return meta;
  } catch {
    removeCacheKeys(STORAGE_META_KEY);
    return null;
  }
};

export const clearStoredData = () => {
  removeCacheKeys(STORAGE_KEY, STORAGE_META_KEY);
};

/* ---------------- Fetch with timeout ---------------- */

const fetchJsonWithTimeout = async (url, timeoutMs) => {
  const controller = new AbortController();
  let timedOut = false;

  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }

    const contentType = (response.headers.get("content-type") || "")
      .split(";")[0]
      .trim()
      .toLowerCase();

    if (
      contentType &&
      contentType !== "application/json" &&
      !/^application\/[a-z0-9!#$&^_.+-]+\+json$/.test(contentType)
    ) {
      throw new Error(`Expected JSON but received ${contentType}.`);
    }

    return extractData(await response.json());
  } catch (error) {
    if (timedOut) {
      const timeoutError = new Error(`Request timed out after ${timeoutMs}ms.`);

      timeoutError.name = "TimeoutError";
      throw timeoutError;
    }

    throw error;
  } finally {
    clearTimeout(timer);
  }
};

/* ---------------- Remote → local → cache ---------------- */

const loadData = async () => {
  try {
    const data = await fetchJsonWithTimeout(API_URL, REMOTE_TIMEOUT_MS);

    storeData(data, API_URL, false);
    return data;
  } catch {
    showLoader("Loading local application data…");
  }

  try {
    const data = await fetchJsonWithTimeout(
      FALLBACK_API_URL,
      FALLBACK_TIMEOUT_MS,
    );

    storeData(data, FALLBACK_API_URL, true);
    return data;
  } catch {
    const cached = getStoredData();

    if (cached !== null) {
      return cached;
    }

    throw new Error(
      "Unable to load configuration. Check your connection and public/api.json.",
    );
  }
};

/* ---------------- Caller cancellation ---------------- */

const abortError = () => {
  const error = new Error("Loading was cancelled.");
  error.name = "AbortError";
  return error;
};

const waitForRequest = (request, signal) => {
  if (!signal) return request;

  return new Promise((resolve, reject) => {
    const onAbort = () => reject(abortError());

    if (signal.aborted) {
      onAbort();
    } else {
      signal.addEventListener("abort", onAbort, { once: true });
    }

    request.then(
      (data) => {
        signal.removeEventListener("abort", onAbort);

        if (signal.aborted) {
          reject(abortError());
        } else {
          resolve(data);
        }
      },
      (error) => {
        signal.removeEventListener("abort", onAbort);
        reject(signal.aborted ? abortError() : error);
      },
    );
  });
};

/* ---------------- Public fetch function ---------------- */

export const fetchAndStoreData = ({ signal } = {}) => {
  if (!isBrowser()) return Promise.resolve(null);

  if (signal?.aborted) {
    return Promise.reject(abortError());
  }

  if (!activeRequest) {
    activeRequest = (async () => {
      showLoader();

      try {
        return await loadData();
      } finally {
        // Remove the overlay on both success and failure.
        hideLoader();
      }
    })();

    const request = activeRequest;

    const release = () => {
      if (activeRequest === request) {
        activeRequest = null;
      }
    };

    request.then(release, release);
  }

  // Cancelling one caller does not interrupt other callers.
  return waitForRequest(activeRequest, signal);
};

/* ---------------- Automatic startup ---------------- */

const initializeApiData = () => {
  fetchAndStoreData().catch((error) => {
    if (error?.name !== "AbortError") {
      console.error("Unable to initialize application data:", error);
    }
  });
};

if (canUseDOM()) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeApiData, {
      once: true,
    });
  } else {
    initializeApiData();
  }
}
